/**
 * What is still left to eat today, answered with things already in the diary.
 *
 * The hardest moment in a cut is nine in the evening with 350 kcal and 45 g of
 * protein left: the arithmetic is easy and doing it is not, so what happens
 * instead is a decision made on feel and a target missed by a hundred grams of
 * something. Every other tracker leaves that moment to the user and offers a
 * search box.
 *
 * The candidates are deliberately drawn from what this person already eats —
 * favourites and the things they log most — rather than from the catalogue at
 * large. A suggestion is only useful if it is in the fridge, and the strongest
 * available signal for that is having eaten it before.
 */

import { type Per100g, round, scaleToGrams } from './macros.js'

export interface FitCandidate {
  id: string
  name: string
  per100g: Per100g
  /** The amount it would be logged as: its own serving, or 100 of its unit. */
  portionG: number
  unit: 'g' | 'ml'
}

export interface FitSuggestion extends FitCandidate {
  kcal: number
  proteinG: number
  /** How much of the outstanding protein this portion would actually close. */
  closesG: number
}

/** Under this there is nothing worth suggesting; the day is finished. */
export const FIT_MIN_REMAINING_KCAL = 120

/**
 * The candidates that fit, best first.
 *
 * Ranked by how much of the protein gap a portion closes, capped at the gap
 * itself — 60 g of protein does not beat 40 g when only 40 is owed, and letting
 * it would rank a whole chicken breast above the yoghurt that finishes the day.
 * Ties, which includes every candidate once the protein is met, fall to the
 * cheapest in calories.
 *
 * Anything that does not fit the remaining energy is dropped rather than
 * ranked low. A suggestion that puts the day over is not a suggestion.
 */
export function suggestFits(input: {
  candidates: readonly FitCandidate[]
  remainingKcal: number
  /** Grams of protein still owed; zero or less ranks on energy alone. */
  remainingProteinG: number
  limit?: number
}): FitSuggestion[] {
  const { candidates, remainingKcal, remainingProteinG, limit = 3 } = input
  if (!Number.isFinite(remainingKcal) || remainingKcal <= 0) return []

  const gap = Math.max(0, remainingProteinG)
  const fitting: FitSuggestion[] = []
  const seen = new Set<string>()

  for (const candidate of candidates) {
    if (seen.has(candidate.id) || candidate.portionG <= 0) continue
    const totals = scaleToGrams(candidate.per100g, candidate.portionG)
    if (totals.kcal <= 0 || totals.kcal > remainingKcal) continue
    seen.add(candidate.id)
    fitting.push({
      ...candidate,
      kcal: round(totals.kcal, 0),
      proteinG: round(totals.protein, 0),
      closesG: round(Math.min(totals.protein, gap), 0),
    })
  }

  return fitting.sort((a, b) => b.closesG - a.closesG || a.kcal - b.kcal).slice(0, limit)
}
